import React from 'react';
import { Card, Descriptions, Tag, Space, Progress, Typography, Tooltip } from 'antd';
import {
  FileTextOutlined,
  NodeIndexOutlined,
  CloudServerOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { TICKET_CLASS_IDS } from '../../types/triage';
import { triageColors } from '../../theme/antd-theme';
import { formatDurationDays } from '../../utils/formatDuration';
import type { Ticket, BatteryStatus, Gateway } from '../../types/triage';

interface InfoCardsProps {
  ticket: Ticket;
  nodeDetail: any | null;
  batteryStatus: BatteryStatus | null;
  // #5: battery snapshot as of the offline moment — what the classifier
  // decided on. Canonical for diagnosis; batteryStatus is "now".
  batteryAtOffline?: BatteryStatus | null;
  gateway: Gateway | null;
}

const formatDuration = formatDurationDays;

const classDisplayMap: Record<number, { label: string; color: string }> = {
  [TICKET_CLASS_IDS.BATTERY_DRAINED]: { label: 'Battery Drained', color: triageColors.batteryDrained },
  [TICKET_CLASS_IDS.HARDWARE_FAULT]: { label: 'Hardware Fault', color: triageColors.hardwareFault },
  [TICKET_CLASS_IDS.HARDWARE_FAULT_EOL]: { label: 'Hardware EOL', color: triageColors.hardwareFaultEol },
  [TICKET_CLASS_IDS.OFFLINE_UNKNOWN]: { label: 'Offline Unknown', color: triageColors.offlineUnknown },
};

const actionColorMap: Record<string, string> = {
  'replace node': '#ff4d4f',
  'update firmware': '#006EB7',
  'on-site investigation': '#FF8200',
  'relocate node': '#faad14',
  'change batteries': '#FF8200',
};

const rootCauseColorMap: Record<string, string> = {
  'hardware issue': '#ff4d4f',
  'battery is too old': '#FF8200',
  'out of range': '#faad14',
  'outdated firmware': '#006EB7',
  'broken/damaged sensor': '#ff4d4f',
  'false alarm': '#8c8c8c',
  'unknown': '#8c8c8c',
};

const nodeTypeColorMap: Record<string, string> = {
  energy: '#FF8200',
  energy_switchless: '#FF8200',
  aircon: '#006EB7',
  aircon_acir: '#006EB7',
  door: '#8c8c8c',
  occupancy: '#00A651',
  compressor: '#722ed1',
  pipe: '#13c2c2',
};

function getBatteryProgressColor(percent: number): string {
  if (percent < 20) return '#ff4d4f';
  if (percent < 40) return '#faad14';
  return '#00A651';
}

const InfoCards: React.FC<InfoCardsProps> = ({ ticket, nodeDetail, batteryStatus, batteryAtOffline, gateway }) => {
  const classDisplay = classDisplayMap[ticket.ticket_class_id];
  const batteryPercent = batteryStatus ? Math.round(Number(batteryStatus.estimated_battery_left_perc)) : 0;
  // Battery "% left" / "months left" come from a mAh-consumption forecast. Once
  // a node goes silent it stops reporting consumption, so the forecast freezes
  // and extrapolates — reading a near-full "% remaining / months left" for a
  // node whose last voltage was flat and that has been dark for months (the
  // 1.89V-but-"94%/87mo" contradiction). Flag the forecast as stale once the
  // node has been silent long enough that it can no longer reflect reality.
  const offlineDays = nodeDetail?.last_seen
    ? dayjs().diff(dayjs(nodeDetail.last_seen), 'day')
    : null;
  const forecastStale = offlineDays != null && offlineDays >= 7;
  const staleTooltip = `Node has been silent ${offlineDays}d. This is a consumption forecast frozen at the last contact — it does not reflect the true remaining charge (compare the voltage reading).`;
  const nodeType = nodeDetail?.node_sub_type || nodeDetail?.node_type || '--';
  const nodeTypeColor = nodeTypeColorMap[nodeType.toLowerCase()] || '#8c8c8c';

  // Flex-wrap instead of antd Row/Col: these cards render inside the detail
  // pane, whose actual width is (viewport - two sidebar Siders) — often far
  // narrower than the viewport itself. antd's Col breakpoints key off
  // viewport width, so `lg` could squeeze 3 cards into ~100px each on a
  // 1366px laptop even though the pane has nowhere near 992px of room.
  // Wrapping on the cards' own min-width reflects the real available space.
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
      {/* Ticket Info Card */}
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <Card
          title={<Space><FileTextOutlined /><span>Issue Info</span></Space>}
          size="small"
          style={{ height: '100%' }}
        >
          <Descriptions column={1} size="small" colon={false}>
            <Descriptions.Item label="Location">
              {ticket.location_name || '--'}
            </Descriptions.Item>
            <Descriptions.Item label="Class">
              {classDisplay ? (
                <Tag color={classDisplay.color}>{classDisplay.label}</Tag>
              ) : (
                <Tag>{ticket.ticket_class || '--'}</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Opened">
              {ticket.ticket_created ? dayjs(ticket.ticket_created).format('YYYY-MM-DD') : '--'}
            </Descriptions.Item>
            <Descriptions.Item label="Last Detected">
              {ticket.last_detected ? dayjs(ticket.last_detected).fromNow() : '--'}
            </Descriptions.Item>
            <Descriptions.Item label="Current Action">
              {ticket.action_name ? (
                <Tag color={actionColorMap[ticket.action_name.toLowerCase()] || 'blue'}>
                  {ticket.action_name}
                </Tag>
              ) : (
                <Typography.Text type="secondary">none</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Current Root Cause">
              {ticket.root_cause_name ? (
                <Tag color={rootCauseColorMap[ticket.root_cause_name.toLowerCase()] || 'orange'}>
                  {ticket.root_cause_name}
                </Tag>
              ) : (
                <Typography.Text type="secondary">unknown</Typography.Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </div>

      {/* Node Data Card */}
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <Card
          title={<Space><NodeIndexOutlined /><span>Node Data</span></Space>}
          size="small"
          style={{ height: '100%' }}
        >
          <Descriptions column={1} size="small" colon={false}>
            <Descriptions.Item label="Battery Voltage">
              {batteryStatus ? (() => {
                const v = Number(batteryStatus.avg_battery_lvl);
                return (
                  <Typography.Text
                    style={{ color: v < 2.2 ? '#ff4d4f' : v < 2.5 ? '#faad14' : '#00A651', fontWeight: 600 }}
                  >
                    {v.toFixed(2)}V
                  </Typography.Text>
                );
              })() : '--'}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Space size={4}>
                  <span>Battery Life</span>
                  <Tooltip title={forecastStale ? staleTooltip : "Estimated remaining battery capacity based on tracked mAh consumption (radio activity, firmware behavior). Not a voltage reading."}>
                    <InfoCircleOutlined style={{ fontSize: 12, color: forecastStale ? '#faad14' : '#8c8c8c' }} />
                  </Tooltip>
                </Space>
              }
            >
              {batteryStatus ? (
                <Tooltip title={forecastStale ? staleTooltip : ''}>
                  <div style={{ opacity: forecastStale ? 0.45 : 1 }}>
                    <Progress
                      percent={batteryPercent}
                      size="small"
                      style={{ maxWidth: 120 }}
                      strokeColor={getBatteryProgressColor(batteryPercent)}
                      format={(p) => `${p}%`}
                    />
                    {forecastStale && (
                      <div style={{ fontSize: 11, color: '#faad14' }}>stale · {offlineDays}d silent</div>
                    )}
                  </div>
                </Tooltip>
              ) : '--'}
            </Descriptions.Item>
            <Descriptions.Item label="Months Left">
              {batteryStatus?.months_of_life_left != null ? (
                forecastStale ? (
                  <Tooltip title={staleTooltip}>
                    <Typography.Text type="warning">
                      {Number(batteryStatus.months_of_life_left)} <span style={{ fontSize: 11 }}>(stale)</span>
                    </Typography.Text>
                  </Tooltip>
                ) : (
                  Number(batteryStatus.months_of_life_left)
                )
              ) : '--'}
            </Descriptions.Item>
            <Descriptions.Item label="Battery Type">
              {batteryStatus ? <Tag>{batteryStatus.battery_type}</Tag> : '--'}
            </Descriptions.Item>
            <Descriptions.Item label="Battery Age">
              {batteryStatus ? formatDuration(batteryStatus.current_age) : '--'}
            </Descriptions.Item>
            {/* #5: prefer the as-of-offline snapshot — that's the battery
                state the classifier decided on and the causally-correct read
                for "why did it go offline". Show "now" only when it differs,
                since the divergence itself is diagnostic (e.g. battery
                re-seated since = tampering signature). */}
            <Descriptions.Item label={batteryAtOffline ? 'Suggested Action (at offline)' : 'Suggested Action'}>
              {batteryAtOffline ? (
                <>
                  <Tag color="orange">{batteryAtOffline.suggested_action}</Tag>
                  {batteryStatus &&
                    batteryStatus.suggested_action !== batteryAtOffline.suggested_action && (
                      <div style={{ marginTop: 4, fontSize: 11, color: '#8c8c8c' }}>
                        now: {batteryStatus.suggested_action}
                      </div>
                    )}
                </>
              ) : batteryStatus ? (
                <Tag color="orange">{batteryStatus.suggested_action}</Tag>
              ) : '--'}
            </Descriptions.Item>
            <Descriptions.Item label="Firmware">
              {nodeDetail?.firmware_version_name || nodeDetail?.current_firmware_version_number || '--'}
            </Descriptions.Item>
            <Descriptions.Item label="Node Type">
              <Tag color={nodeTypeColor}>{nodeType}</Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </div>

      {/* Gateway Status Card */}
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <Card
          title={<Space><CloudServerOutlined /><span>Gateway</span></Space>}
          size="small"
          style={{ height: '100%' }}
        >
          {gateway ? (
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item label="Device">
                <Typography.Text code style={{ fontSize: 12 }}>
                  {gateway.balena_device_name || gateway.balena_id}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {gateway.is_online ? (
                  <Tag icon={<CheckCircleOutlined />} color="success">Online</Tag>
                ) : (
                  <Tag icon={<CloseCircleOutlined />} color="error">Offline</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Last Heartbeat">
                {gateway.last_heartbeat_time
                  ? dayjs(gateway.last_heartbeat_time).fromNow()
                  : '--'}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: '20px 0' }}>
              No gateway info available
            </Typography.Text>
          )}
        </Card>
      </div>
    </div>
  );
};

export default InfoCards;
